import { useState } from 'react'
import { Link } from 'react-router-dom'
import { playScanSound } from '../../services/sounds'
import { hapticLight } from '../../utils/haptic'

const PAINT_OBJECTS = [
  { id: 'table', name: 'Table', emoji: '🪑' },
  { id: 'box', name: 'Box', emoji: '📦' },
  { id: 'birdhouse', name: 'Birdhouse', emoji: '🐦' },
] as const

const PAINT_COLORS = [
  { id: 'red', name: 'Red', emoji: '🔴', hex: '#e53935' },
  { id: 'blue', name: 'Blue', emoji: '🔵', hex: '#1e88e5' },
  { id: 'green', name: 'Green', emoji: '🟢', hex: '#43a047' },
  { id: 'yellow', name: 'Yellow', emoji: '🟡', hex: '#fdd835' },
  { id: 'purple', name: 'Purple', emoji: '🟣', hex: '#8e24aa' },
  { id: 'orange', name: 'Orange', emoji: '🟠', hex: '#fb8c00' },
] as const

export function PaintView() {
  const [step, setStep] = useState<'object' | 'color' | 'done'>('object')
  const [object, setObject] = useState<typeof PAINT_OBJECTS[number] | null>(null)
  const [color, setColor] = useState<typeof PAINT_COLORS[number] | null>(null)

  const handlePickObject = (obj: typeof PAINT_OBJECTS[number]) => {
    hapticLight()
    playScanSound()
    setObject(obj)
    setStep('color')
  }

  const handlePickColor = (c: typeof PAINT_COLORS[number]) => {
    hapticLight()
    playScanSound()
    setColor(c)
    setStep('done')
  }

  const handleReset = () => {
    setObject(null)
    setColor(null)
    setStep('object')
  }

  if (step === 'done' && object && color) {
    return (
      <div className="page page--paint page--paint-done">
        <div className="paint-done-content">
          <div
            className="paint-done-preview"
            style={{ backgroundColor: color.hex }}
            aria-hidden
          >
            <span className="paint-done-emoji">{object.emoji}</span>
          </div>
          <h1 className="paint-done-title">You painted it! 🎨</h1>
          <p className="paint-done-name">
            Your {object.name.toLowerCase()} is {color.name.toLowerCase()}!
          </p>
        </div>
        <div className="paint-done-actions">
          <button type="button" className="btn-large btn-primary" onClick={handleReset} style={{ textDecoration: 'none' }}>
            Paint something else
          </button>
          <Link to="/hardware" className="btn-large btn-secondary" style={{ textDecoration: 'none' }}>
            Back to Hardware Store
          </Link>
        </div>
      </div>
    )
  }

  if (step === 'color') {
    return (
      <div className="page page--paint">
        <header className="paint-header">
          <button type="button" className="btn-back" onClick={() => setStep('object')} aria-label="Back">←</button>
          <h1 className="page-title">Pick a color</h1>
        </header>
        <p className="paint-prompt">What color will your {object?.name.toLowerCase()} be?</p>
        <div className="paint-color-grid" aria-label="Color choices">
          {PAINT_COLORS.map((c) => (
            <button
              key={c.id}
              type="button"
              className="paint-color-swatch"
              style={{ backgroundColor: c.hex }}
              onClick={() => handlePickColor(c)}
              aria-label={`Paint ${c.name}`}
              title={c.name}
            >
              <span className="paint-color-emoji" aria-hidden>{c.emoji}</span>
            </button>
          ))}
        </div>
        <Link to="/hardware" className="btn-large btn-secondary" style={{ textDecoration: 'none', marginTop: 24 }}>
          Back to Hardware Store
        </Link>
      </div>
    )
  }

  return (
    <div className="page page--paint">
      <header className="paint-header">
        <Link to="/hardware" className="btn-back" aria-label="Back to hardware store">←</Link>
        <h1 className="page-title">🎨 Paint something!</h1>
      </header>
      <p className="paint-prompt">What do you want to paint?</p>
      <ul className="paint-object-grid" aria-label="Things to paint">
        {PAINT_OBJECTS.map((obj) => (
          <li key={obj.id}>
            <button
              type="button"
              className="paint-object-card"
              onClick={() => handlePickObject(obj)}
              aria-label={`Paint the ${obj.name}`}
            >
              <span className="paint-object-emoji" aria-hidden>{obj.emoji}</span>
              <span className="paint-object-name">{obj.name}</span>
            </button>
          </li>
        ))}
      </ul>
      <Link to="/hardware" className="btn-large btn-secondary" style={{ textDecoration: 'none', marginTop: 24 }}>
        Back to Hardware Store
      </Link>
    </div>
  )
}
